import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { PurchaseOrder, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { GlowCard } from '../components/ui/GlowCard'
import { Drawer } from '../components/ui/Drawer'
import { useProjectOptions } from '../hooks/useEntityOptions'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const STATUSES = ['draft', 'submitted', 'partial', 'received', 'cancelled'] as const

export default function PurchaseOrders() {
  const [orders, setOrders] = useState<PurchaseOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [formData, setFormData] = useState({ project_slug: '', supplier: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const { options: projectOptions } = useProjectOptions()

  const loadOrders = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' })
      if (status) params.set('status', status)
      const result = await api.get<PaginatedResult<PurchaseOrder>>(`/purchase-orders?${params}`)
      setOrders(result.data)
      setTotalPages(result.totalPages)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [page, status])

  useEffect(() => { loadOrders() }, [loadOrders])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await api.post('/purchase-orders', { ...formData, items: [] })
      setDrawerOpen(false)
      setFormData({ project_slug: '', supplier: '', notes: '' })
      loadOrders()
    } catch { /* error shown */ }
    finally { setSaving(false) }
  }

  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title" style={{ fontSize: 22 }}>Purchase Orders</h1>
        <Button icon={PlusIcon} onClick={() => setDrawerOpen(true)}>New PO</Button>
      </div>

      <div className="mb-4">
        <div className="v3-select-wrap max-w-[160px]">
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }} className="v3-select" style={{ fontSize: 14 }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
          <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
        </div>
      </div>

      {loading ? (
        <TableSkeleton cols={5} />
      ) : orders.length === 0 ? (
        <EmptyState
          title="No purchase orders yet — create one when needed"
          description="Create your first purchase order."
          action={<Button onClick={() => setDrawerOpen(true)}>New PO</Button>}
        />
      ) : (
        <>
          <GlowCard style={{ padding: 0 }} className="hidden sm:block">
            <table className="v3-table">
              <thead>
                <tr>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>PO #</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Supplier</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Status</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }} className="text-right">Subtotal</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Expected Delivery</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((po) => (
                  <tr key={po.id}>
                    <td className="mono" style={{ color: 'var(--primary)', fontWeight: 500, fontSize: 14, fontFamily: 'var(--font-mono)' }}>{po.po_number ?? po.slug}</td>
                    <td style={{ color: 'var(--text)', fontSize: 14 }}>{po.supplier}</td>
                    <td><StatusBadge status={po.status} /></td>
                    <td className="text-right mono" style={{ fontWeight: 500, fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>${po.subtotal.toLocaleString()}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlowCard>

          <div className="sm:hidden space-y-3">
            {orders.map((po) => (
              <div key={po.id} className="mobile-card">
                <div className="flex justify-between items-start">
                  <p className="mono" style={{ fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-mono)', color: 'var(--primary)' }}>{po.po_number ?? po.slug}</p>
                  <StatusBadge status={po.status} />
                </div>
                <div className="mobile-card-row mt-1">
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{po.supplier}</span>
                  <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>${po.subtotal.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{page} / {totalPages}</span>
              <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
            </div>
          )}
        </>
      )}

      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title="New Purchase Order">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Project *</label>
            <div className="v3-select-wrap">
              <select value={formData.project_slug} onChange={(e) => setFormData({ ...formData, project_slug: e.target.value })} className="v3-select" style={{ fontSize: 14 }} required>
                <option value="">Select project...</option>
                {projectOptions.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
          </div>
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Supplier *</label>
            <input value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} className="v3-input" style={{ fontSize: 14 }} required />
          </div>
          <div>
            <label className="v3-field-label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="v3-textarea" style={{ fontSize: 14 }} rows={3} />
          </div>
          <Button type="submit" loading={saving} style={{ width: '100%', fontSize: 14 }}>
            Create Purchase Order
          </Button>
        </form>
      </Drawer>
    </div>
  )
}
